import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
} from '@material-ui/core'
import Table from '@material-ui/core/Table'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import Input from '@material-ui/core/Input'
import PropTypes from 'prop-types'
import IconButton from '@material-ui/core/IconButton'
import { CancelOutlined } from '@material-ui/icons'
import moment from 'moment'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import { StyledTableHead } from '../Approvals/ProjectsView.styles'
import { StyledApproveButton, StyledMutedButton } from '../Approvals/ApprovalView.styles'
import { splitTimeEntry } from '../../entities/timeEntry/service'

const InvoiceActionButtons = ({ rates, onSplitEntry, client, onUpdateManyEntries, entry }) => {
  const [entryToSplit, setEntryToSplit] = useState(null)

  const handleSplitEntry = entry => {
    setEntryToSplit(entry)
  }

  const onWriteOff = async (entry, reverse = false) => {
    onUpdateManyEntries([
      {
        ...entry,
        status: !reverse ? 'WRITEOFF' : 'RAW',
      },
    ])
  }

  const onDefer = async entryToDefer => {
    let deferToMonth
    const isDefered = !!entryToDefer.deferToMonth

    if (isDefered) {
      const momentDefer = moment.utc(entryToDefer.deferToMonth)

      if (momentDefer.isSame(moment.utc(), 'month')) {
        deferToMonth = moment.utc().add(1, 'month')
      }

      if (momentDefer.isAfter(moment.utc(), 'month')) {
        deferToMonth = moment.utc()
      }
    } else deferToMonth = moment.utc().add(1, 'month')

    onUpdateManyEntries([
      {
        ...entryToDefer,
        deferToMonth: deferToMonth ? deferToMonth.toISOString() : null,
      },
    ])
  }

  const isDeferedToAnotherMonth = useMemo(() => {
    if (!entry.deferToMonth) return false
    const momentDefer = moment.utc(entry.deferToMonth)
    return momentDefer.isAfter(moment.utc(), 'month')
  }, [entry])

  return (
    <div style={{ gap: 4, display: 'flex' }}>
      {entry.status === 'WRITEOFF' && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <IconButton onClick={() => onWriteOff(entry, true)}>
            <CancelOutlined />
          </IconButton>
          <span> Write Off</span>
        </div>
      )}
      {isDeferedToAnotherMonth && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <IconButton onClick={() => onDefer(entry)}>
            <CancelOutlined />
          </IconButton>
          <span>Deferred to {moment.utc(entry.deferToMonth).format('MMMM - YYYY')}</span>
        </div>
      )}
      {entry.status !== 'WRITEOFF' && !isDeferedToAnotherMonth && (
        <div style={{ display: 'flex', gap: 4 }}>
          <StyledMutedButton
            onClick={() => {
              onWriteOff(entry)
            }}
          >
            Write Off
          </StyledMutedButton>
          <StyledMutedButton
            onClick={() => {
              onDefer(entry)
            }}
          >
            Defer
          </StyledMutedButton>
          <StyledMutedButton onClick={() => handleSplitEntry(entry)}>Split</StyledMutedButton>
        </div>
      )}
      <SplitEntriesDialog
        open={entryToSplit}
        onAccept={async (timeEntry, splitEntries, actions) => {
          const durations = splitEntries.map(e => Number(e.durationInHours))
          const statusses = actions.map(action => {
            if (action === 'writeoff') return 'WRITEOFF'
            if (action === 'defer') return timeEntry.status
            return timeEntry.status
          })

          const comment = splitEntries[0].comment

          const splitResult = await splitTimeEntry({
            client,
            timeEntryId: timeEntry._id,
            durationInHours: durations,
            comment,
            statusses,
          })

          await Promise.all(
            actions.map(async (action, idx) => {
              if (action === 'defer') {
                const entryToDefer = idx === 0 ? splitResult.parent : splitResult.child
                await onDefer(entryToDefer)
              }
            }),
          )

          setEntryToSplit(null)
          onSplitEntry(splitResult)
        }}
        entryToSplit={entryToSplit}
        onCancel={() => setEntryToSplit(null)}
        rates={rates[entry.projectId]}
      />
    </div>
  )
}

const splitEntry = entry => {
  const half = Number(entry.durationInHours) / 2
  return [
    {
      ...entry,
      durationInHours: half,
    },
    {
      ...entry,
      durationInHours: half,
    },
  ]
}

const SplitEntriesDialog = ({ open, onAccept, onCancel, entryToSplit, rates }) => {
  const [entries, setEntries] = useState([])
  const [actions, setActions] = useState([])

  useEffect(() => {
    if (entryToSplit) {
      setEntries(splitEntry(entryToSplit))
      setActions(['bill', 'writeoff'])
    }
  }, [entryToSplit])

  const handleChangeHours = (idx, rawValue) => {
    const value = parseFloat(rawValue) || 0
    const newEntries = [...entries]
    newEntries[idx].durationInHours = value
    setEntries(newEntries)
  }

  const handleActionChange = (idx, value) => {
    setActions(prev => {
      const next = [...prev]
      next[idx] = value
      return next
    })
  }

  if (!open || !entryToSplit) return null

  const totalEnteredHours = entries.reduce((sum, e) => sum + Number(e.durationInHours), 0)
  const maxHours = entryToSplit.durationInHours
  const isValid = totalEnteredHours <= maxHours

  // Compose split entries with status for backend
  const splitEntriesWithAction = entries.map((e, idx) => {
    let status
    if (actions[idx] === 'writeoff') status = 'WRITEOFF'
    else if (actions[idx] === 'defer') status = "'RAW'"
    else status = 'RAW'

    return {
      ...e,
      durationInHours: Number(e.durationInHours),
      status,
      comment: e.comment,
    }
  })

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Split entries</DialogTitle>
      <DialogContent>
        <Table aria-label="simple table">
          <StyledTableHead>
            <TableRow>
              <TableCell width={200}>Comment</TableCell>
              <TableCell width={200}>Action</TableCell>
              <TableCell width={200}>Total (Rate * Hours)</TableCell>
              <TableCell width={50}>Hours</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {entries.map((entry, idx) => (
              <TableRow key={`${entry._id}-${idx}`}>
                <TableCell>{entry.comment}</TableCell>
                <TableCell>
                  <Select
                    value={actions[idx] || 'bill'}
                    onChange={ev => handleActionChange(idx, ev.target.value)}
                  >
                    <MenuItem value="bill">Bill</MenuItem>
                    <MenuItem value="writeoff">Write Off</MenuItem>
                    <MenuItem value="defer">Defer</MenuItem>
                  </Select>
                  {actions[idx] === 'defer' && (
                    <span style={{ fontSize: 12, marginLeft: 8 }}>
                      {moment
                        .utc()
                        .add(1, 'month')
                        .format('MMMM YYYY')}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {rates[entry.personId]} * {entry.durationInHours} ={' '}
                  {(entry.durationInHours * rates[entry.personId]).toFixed(2)}
                </TableCell>
                <TableCell width={80}>
                  <Input
                    type="number"
                    inputProps={{ min: 0 }}
                    value={entry.durationInHours}
                    onChange={ev => handleChangeHours(idx, ev.target.value)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!isValid && (
          <FormHelperText error>
            Sum of hours must be less than or equal to {maxHours}
          </FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <StyledMutedButton onClick={onCancel}>Cancel</StyledMutedButton>
        <StyledApproveButton
          onClick={() => onAccept(entryToSplit, splitEntriesWithAction, actions)}
          disabled={!isValid}
        >
          Confirm
        </StyledApproveButton>
      </DialogActions>
    </Dialog>
  )
}

SplitEntriesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onAccept: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  entryToSplit: PropTypes.object.isRequired,
  rates: PropTypes.object.isRequired,
}

InvoiceActionButtons.propTypes = {
  onSplitEntry: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  entry: PropTypes.object.isRequired,
}

export default InvoiceActionButtons
